import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedBenchmark } from '../../redux/userReducer';
import { Card, CardHeader, CardContent, Typography, Box, Select, MenuItem } from '@mui/material';
import { TrendingUp, KeyboardArrowDown } from '@mui/icons-material';
import Spinner from 'components/Spinner';
import styles from './PortfolioBenchmarkComparison.module.sass';

// Constants
const PORTFOLIO_COLOR = '#73F6FF';
const BENCHMARK_COLOR = '#9F2DEF';

const PortfolioBenchmarkComparison = ({ data, isLoading }) => {
  const dispatch = useDispatch();
  const selectedBenchmark = useSelector((state) => state.user.selectedBenchmark);

  if (isLoading) {
    return (
      <Box className={styles.container}>
        <Card className={styles.cyberCard}>
          <Box className={styles.loadingBox}>
            <Spinner sx={{ width: 40, height: 40 }} />
          </Box>
        </Card>
      </Box>
    );
  }

  if (!data?.Benchmark?.values || !data?.Portfolio?.values) return null;

  const chartData = data.Benchmark.values.map((benchmarkPoint) => {
    const portfolioPoint = data.Portfolio.values.find(
      (p) => p.timestamp === benchmarkPoint.timestamp
    );
    
    // Fix timezone issue by adding 'T00:00:00Z' to ensure correct UTC date
    const dateWithTime = `${benchmarkPoint.timestamp}T00:00:00Z`;
    
    return {
      date: dateWithTime,
      displayDate: benchmarkPoint.timestamp, // Keep original for display
      Benchmark: benchmarkPoint.value,
      Portfolio: portfolioPoint?.quote
    };
  });

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return '-';
    return `${value.toFixed(2)}%`;
  };

  const CustomTooltipContent = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    
    // Format the date for display
    const displayDate = label;
    const parts = displayDate.split('-');
    const formattedDate = `${parseInt(parts[1])}/${parseInt(parts[2])}/${parts[0]}`;
    
    return (
      <div className={styles.customTooltip}>
        <div className={styles.label}>{formattedDate}</div>
        {payload.map((entry, index) => (
          <div key={index} className={styles.value} style={{ color: entry.color }}>
            {entry.name}: {formatCurrency(entry.value)}
          </div>
        ))}
      </div>
    );
  };

  const isOutperformerBenchmark = data.outperformer === 'Benchmark';
  const performanceColor = isOutperformerBenchmark ? BENCHMARK_COLOR : PORTFOLIO_COLOR;
  const badgeClassName = `${styles.outperformerBadge} ${isOutperformerBenchmark ? styles.benchmark : ''}`;
  const valueClassName = isOutperformerBenchmark ? `${styles.metricValue} ${styles.benchmark}` : styles.metricValue;

  return (
    <Box className={styles.container}>
      <Card className={styles.cyberCard}>
        <div className={badgeClassName}>
          <TrendingUp />
          {data.outperformer}
        </div>
        
        <CardHeader 
          className={styles.cardHeader}
          title={
            <Box className={styles.headerBox}>
              <Typography className={styles.titleText}>
                {data.message}
              </Typography>
              <div className={styles.benchmarkInfo}>
                <Select
                  value={selectedBenchmark}
                  onChange={(e) => dispatch(setSelectedBenchmark(e.target.value))}
                  size="small"
                  className={styles.customDropdown}
                  MenuProps={{
                    classes: {
                      paper: styles.dropdownMenu
                    }
                  }}
                  IconComponent={(props) => (
                    <KeyboardArrowDown {...props} className={styles.dropdownArrow} />
                  )}
                >
                  {['ETH', 'WBTC', 'BNB', 'LINK', 'UNI', 'PEPE'].map((item) => (
                    <MenuItem 
                      key={item} 
                      value={item}
                      className={`${styles.dropdownItem} ${selectedBenchmark === item ? styles.selectedItem : ''}`}
                    >
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </Box>
          }
        />
        <CardContent>
          <div className={styles.chartContainer}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="rgba(115, 246, 255, 0.1)"
                />
                <XAxis 
                  dataKey="displayDate" 
                  stroke="rgba(115, 246, 255, 0.5)"
                  tick={{ fill: 'rgba(115, 246, 255, 0.8)', fontSize: 12 }}
                  tickFormatter={(value) => {
                    // Split YYYY-MM-DD and return MM/DD format
                    const parts = value.split('-');
                    return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
                  }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={formatCurrency}
                  stroke="rgba(115, 246, 255, 0.5)"
                  tick={{ fill: 'rgba(115, 246, 255, 0.8)', fontSize: 12 }}
                />
                <Tooltip content={<CustomTooltipContent />} />
                <Legend 
                  wrapperStyle={{
                    color: 'rgba(115, 246, 255, 0.8)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Portfolio" 
                  stroke={PORTFOLIO_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, stroke: PORTFOLIO_COLOR, strokeWidth: 2 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="Benchmark" 
                  stroke={BENCHMARK_COLOR}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, stroke: BENCHMARK_COLOR, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.metricBox}>
              <div className={styles.metricLabel}>Total Outperformance</div>
              <div className={styles.metricContent}>
                <div className={valueClassName}>
                  {formatPercentage(data.outperformance_percentage)}
                </div>
                <div className={styles.metricSubtext}>
                  {data.duration || 'Total Period'}
                </div>
              </div>
            </div>
            
            <div className={styles.metricBox}>
              <div className={styles.metricLabel}>Annualized Return</div>
              <div className={styles.metricContent}>
                <div className={valueClassName}>
                  {formatPercentage(data.annualized_outperformance_percentage)}
                </div>
                <div className={styles.metricSubtext}>Projected Annual</div>
              </div>
            </div>

            <div className={styles.metricBox}>
              <div className={styles.metricLabel}>Value Difference</div>
              <div className={styles.metricContent}>
                <div className={valueClassName}>
                  {formatCurrency(data.delta)}
                </div>
                <div className={styles.metricSubtext}>Total Delta</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PortfolioBenchmarkComparison;
